import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback } from 'react'
import toast from 'react-hot-toast'

import { getLocationByLatLngV2 } from 'api/geo'
import { clientFetch } from 'lib/fetch/client'
import { useIpLocationStore } from 'store/server-context/ip-location'
import getGeoLocation from 'util/geGeoLocation'

const GPS_KEY = 'gps'

const useGeoLocation = () => {
  const queryClient = useQueryClient()
  const ipLocation = useIpLocationStore(state => state.ipLocation)
  const setUserLocation = useIpLocationStore(state => state.setUserLocation)
  const locale = useLocale()
  const t = useTranslations()

  const gpsQuery = useQuery({
    queryKey: [GPS_KEY],
    queryFn: async () => {
      try {
        const { latitude, longitude } = await getGeoLocation()

        return {
          lng: longitude,
          lat: latitude,
          permission: true
        }
      } catch (error) {
        toast(t('common.no_location_permission'), {
          duration: 3000
        })
        return {
          lng: 0,
          lat: 0,
          permission: false
        }
      }
    }
  })

  const locationQuery = useQuery({
    queryKey: ['geo/location', gpsQuery.data?.lat, gpsQuery.data?.lng],
    queryFn: async () => {
      const gpsData = gpsQuery.data!
      if (!gpsData.permission) {
        return ipLocation
      }

      const { data } = await getLocationByLatLngV2(
        clientFetch,
        `${gpsData.lat},${gpsData.lng}`,
        locale
      )
      const locationData = {
        ...data,
        ip_geo_point: gpsData
      }

      setUserLocation(locationData)

      return locationData
    },
    enabled: !gpsQuery.isLoading
  })

  const updateGps = useCallback(
    (location: GeolocationCoordinates) => {
      queryClient.setQueryData([GPS_KEY], {
        lng: location.longitude,
        lat: location.latitude,
        permission: true
      })
    },
    [queryClient]
  )

  const isLoading = gpsQuery.isLoading || locationQuery.isLoading
  const yourLocation = locationQuery.data

  return {
    isLoading,
    permission: gpsQuery.data?.permission,
    yourLocation,
    updateGps
  }
}

export default useGeoLocation
